

import React, { useEffect, useState, useRef } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SelectPrimaryProfile from "../SelectCompetitorProfile";
import SelectSecondaryProfile from "../SelectCompetitorProfile";
import SocialPopup from "../SocialPopup";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";
import ReviewAndGenerateSnapshot from "../ReviewAndGenerateSnapshot";
import UserService from "services/user-service";
import WorkspaceService from "services/workspace-service";
import SnapshotService from "services/snapshot-service";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import CircularProgress from "@mui/material/CircularProgress";
// import SnapshotLoading from "../SnapshotLoading"; // [DELETE]
import SnapshotStepper from "../Stepper/SnapshotStepper";
import MDSnackbar from "components/MDSnackbar";

dayjs.extend(utc);

function MarketSnapshot({ goBack, onComplete, editSnapshotData, entitlements, setGuidanceMessage, isLiveDemo, showGuidance }) {
  const [primaryProfile, setPrimaryProfile] = useState(null);
  const [secondaryProfiles, setSecondaryProfiles] = useState([]);
  const [step, setStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showEmailError, setShowEmailError] = useState(false); // [NEW] Inline error state

  const openErrorSB = (msg) => {
    setErrorMessage(msg);
    setErrorSB(true);
  };
  const closeErrorSB = () => setErrorSB(false);

  // [NEW] Combined flag for highlights
  const enableDemoHighlight = isLiveDemo && showGuidance;

  const isMarketEntitled = true;
  const [navHeight, setNavHeight] = useState(75);
  const [isTransparent, setIsTransparent] = useState(true);
  const snapshotConfigRef = useRef({});

  const steps = [
    "Select Snapshot Type",
    "Select Primary Profile",
    "Select Secondary Profiles",
    isEditing ? "Review & Save Snapshot" : "Review & Save Snapshot",
  ];

  // ───────────────────────────────
  // 🟢 Guidance Message Logic
  // ───────────────────────────────
  useEffect(() => {
    if (setGuidanceMessage) {
      if (step === 1) setGuidanceMessage("Select Primary Profile");
      else if (step === 2) setGuidanceMessage("Select Secondary Profiles");
      else if (step === 3) setGuidanceMessage("Review & Save Snapshot");
    }
  }, [step, setGuidanceMessage]);


  // ───────────────────────────────
  // 🟢 Prefill data if editing snapshot
  // ───────────────────────────────
  useEffect(() => {
    if (editSnapshotData) {
      console.log("🟡 Editing existing Market Snapshot:", editSnapshotData);

      setIsEditing(true);
      setStep(3); // skip straight to review step

      snapshotConfigRef.current.snapshotId = editSnapshotData._id || editSnapshotData.snapshotId;
      snapshotConfigRef.current.snapshotName = editSnapshotData.snapshot_name || editSnapshotData.snapshotName;

      // ✅ Handle recipient_emails from backend vs email field in config
      if (Array.isArray(editSnapshotData.recipient_emails)) {
        snapshotConfigRef.current.email = editSnapshotData.recipient_emails.join(", ");
      } else {
        snapshotConfigRef.current.email = editSnapshotData.recipient_emails || editSnapshotData.email || "";
      }
      snapshotConfigRef.current.scheduleMonthly = editSnapshotData.scheduleMonthly;

      if (editSnapshotData.primaryProfile) {
        setPrimaryProfile(editSnapshotData.primaryProfile);
      }
      if (Array.isArray(editSnapshotData.secondaryProfiles)) {
        setSecondaryProfiles(editSnapshotData.secondaryProfiles);
      }
    }
  }, [editSnapshotData]);

  // ───────────────────────────────
  // Navbar height & scroll effects
  // ───────────────────────────────
  useEffect(() => {
    const navbarEl = document.querySelector("header.MuiAppBar-root");
    if (navbarEl) setNavHeight(navbarEl.offsetHeight);

    const resizeObserver = new ResizeObserver(() => {
      if (navbarEl) setNavHeight(navbarEl.offsetHeight);
    });
    if (navbarEl) resizeObserver.observe(navbarEl);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsTransparent(window.scrollY === 0);
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ───────────────────────────────
  // 🟢 Main Action Handler
  // ───────────────────────────────
  const handlePrimarySelect = (profiles) => {
    if (profiles.length > 0) setPrimaryProfile(profiles[0]);
    else setPrimaryProfile(null);
  };

  const handleSecondarySelect = (profiles) => {
    setSecondaryProfiles(profiles);
  };

  const handleContinue = async () => {
    if (step === 1) return setStep(2);
    if (step === 2) return setStep(3);

    if (step === 3) {
      // 🛡️ [VALIDATION] Ensure emails are provided if immediate send is checked
      const emailsArr = snapshotConfigRef.current.email
        ?.split(",")
        .map((e) => e.trim())
        .filter(Boolean);

      if ((snapshotConfigRef.current.sendSnapshotImmediately || snapshotConfigRef.current.scheduleMonthly) && (!emailsArr || emailsArr.length === 0)) {
        setShowEmailError(true);
        return;
      }

      setIsSaving(true);
      try {
        const rangeStart = dayjs.utc().subtract(1, "month").startOf("month").toDate();
        const rangeEnd = dayjs.utc().subtract(1, "month").endOf("month").toDate();
        const nextRunAt = snapshotConfigRef.current.scheduleMonthly
          ? dayjs.utc().endOf("month").add(1, "day").startOf("day").toDate()
          : null;

        const snapshotInfo = {
          snapshotType: "Market",
          snapshotName: snapshotConfigRef.current.snapshotName,
          emails: snapshotConfigRef.current.email
            ?.split(",")
            .map((e) => e.trim())
            .filter(Boolean),
          primaryProfile: {
            profileId: primaryProfile?.id,
            platform: primaryProfile?.platform,
          },
          secondaryProfiles: secondaryProfiles.map((p) => ({
            profileId: p.id,
            platform: p.platform,
          })),
          frequency: snapshotConfigRef.current.scheduleMonthly ? "monthly" : "once",
          scheduleEnabled: snapshotConfigRef.current.scheduleMonthly,
          rangeStart,
          rangeEnd,
          nextRunAt,
          branding_pack_id: snapshotConfigRef.current.branding_pack_id || null, // [FIX] Include selected branding pack
          target_client: snapshotConfigRef.current.target_client || null,        // [FIX] Include target client
        };

        const existingSnapshotId = snapshotConfigRef.current.snapshotId;

        if (isEditing && existingSnapshotId) {
          console.log(`🟡 Updating existing Market Snapshot metadata: ${existingSnapshotId}`);
          await WorkspaceService.updateWorkspaceSnapshot(existingSnapshotId, snapshotInfo);
          console.log("✅ Market Snapshot metadata updated successfully");
        } else {
          console.log("🆕 Creating new Market Snapshot...");
          if (!isLiveDemo) {
            await WorkspaceService.saveWorkspaceSnapshot(snapshotInfo);
            console.log("✅ Market Snapshot saved successfully");
          } else {
            console.log("⚠️ Live Demo: Downloading Market Snapshot PDF locally instead of saving");
            await SnapshotService.generateMarketSnapshot(snapshotInfo);
          }
        }

        // 📧 Handle Immediate Email Dispatch [NEW]
        console.log("🔍 [DEBUG] sendSnapshotImmediately:", snapshotConfigRef.current.sendSnapshotImmediately);
        if (snapshotConfigRef.current.sendSnapshotImmediately) {
          console.log("🚀 [DEBUG] User requested immediate snapshot email. Payload:", { snapshotInfo, emails: snapshotInfo.emails });
          try {
            const emailRes = await SnapshotService.emailSnapshot(snapshotInfo, snapshotInfo.emails);
            console.log("✅ [DEBUG] Immediate snapshot email response:", emailRes);
          } catch (emailErr) {
            console.error("⚠️ [DEBUG] Immediate snapshot email failed:", emailErr);
          }
        }

        // ✅ Notify parent component on completion
        onComplete();
      } catch (err) {
        console.error("❌ Failed to save/update Market Snapshot:", err);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const renderErrorSB = (
    <MDSnackbar
      color="error"
      icon="warning"
      title="Validation Error"
      content={errorMessage}
      open={errorSB}
      onClose={closeErrorSB}
      close={closeErrorSB}
      bgWhite
    />
  );


  // const handleBack = () => {
  //   if (step === 1) return goBack();
  //   setStep(step - 1);
  // };

  // ───────────────────────────────
  // 🔙 Back Handler
  // ───────────────────────────────
  const handleBack = () => {
    // ✏️ Edit mode: always go back to the list page (SocialStudio)
    if (isEditing) {
      onComplete(); // parent handles hiding builder + reloading list
      return;
    }

    // ➕ Create mode: normal step-wise back navigation
    if (step === 1) return goBack(); // from step 1 → snapshot type selection

    // Clear inline error if going back from review step
    if (step === 3) setShowEmailError(false);

    setStep(step - 1);
  };

  const handleClear = () => {
    if (step === 1) setPrimaryProfile(null);
    if (step === 2) setSecondaryProfiles([]);
  };

  const selectedProfiles =
    step === 1
      ? primaryProfile
        ? [primaryProfile]
        : []
      : step === 2
        ? secondaryProfiles
        : [primaryProfile, ...secondaryProfiles];

  // ───────────────────────────────
  // Loading state - [MOVED TO BUTTON LEVEL]
  // ───────────────────────────────

  // ───────────────────────────────
  // Render Component
  // ───────────────────────────────
  return (
    <MDBox textAlign="center" mt={-5}>
      {/* 🧭 Sticky Stepper Toolbar */}
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        width="100%"
        bgcolor="white"
        py={1}
        px={1}
        sx={{
          position: "sticky",
          top: `${navHeight - 1}px`,
          zIndex: 999,
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          gap: 2,
          mb: 1,
          boxShadow: isTransparent ? "none" : "0 4px 12px rgba(0, 0, 0, 0.2)",
          transition:
            "top 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease",
        }}
      >
        <IconButton onClick={handleBack} sx={{ color: "primary.main", ml: 1 }}>
          <ArrowBackIcon />
        </IconButton>

        <MDBox flexGrow={1} pl={2}>
          <SnapshotStepper steps={steps} activeStep={step} />
        </MDBox>
      </MDBox>

      {/* Step 1: Select Primary Profile */}
      {step === 1 && (
        <SelectPrimaryProfile
          selectedProfiles={primaryProfile ? [primaryProfile] : []}
          onSelect={handlePrimarySelect}
          isLiveDemo={isLiveDemo}        // [NEW]
          isStep1={true}                 // [NEW]
          showGuidance={showGuidance}
          maxSelection={1}               // [FIX] Enable auto-replace logic
          isDemoHighlight={enableDemoHighlight && !primaryProfile} // [NEW] Highlight first item if no selection
        />
      )}

      {/* Step 2: Select Secondary Profiles */}
      {step === 2 && (
        <SelectSecondaryProfile
          onSelect={handleSecondarySelect}
          selectedProfiles={secondaryProfiles}
          maxSelection={2} // [RESTORED] Limit to 2 per user request
          excludeIds={primaryProfile ? [primaryProfile.id] : []} // [RESTORED]
          filterPlatform={primaryProfile?.platform} // [RESTORED]
          title="Select Competitors"
          description="Choose up to 2 competitor profiles to compare against." // [UPDATED]
          isDemoHighlight={enableDemoHighlight}
          isStep2={true}
        />
      )}

      {/* Step 3: Review & Generate / Save Snapshot */}
      {step === 3 && (
        <MDBox pt={6} pb={3}>
          <ReviewAndGenerateSnapshot
            snapshotType="Market"
            primaryProfile={primaryProfile}
            secondaryProfiles={secondaryProfiles}
            snapshotConfigRef={snapshotConfigRef}
            snapshotId={snapshotConfigRef.current.snapshotId || null}
            entitlements={entitlements}
            isLiveDemo={isLiveDemo} // [NEW]
            forceEmailError={showEmailError} // [NEW]
            onSave={handleContinue}
            onCancel={handleBack}
            isSaving={isSaving}
            demoHighlight={enableDemoHighlight} // [NEW] Pass highlight state
          />
        </MDBox>
      )}

      {/* Floating Action Popup (Hidden in Step 3 review to avoid double buttons) */}
      {step < 3 && (
        <SocialPopup
          selectedItems={selectedProfiles}
          onClearConfirm={step < 3 ? handleClear : undefined}
          onContinue={handleContinue}
          onBack={handleBack}
          actionLabel={step === 3 ? "Save Snapshot" : "Continue"}
          entityType="profile"
          isEditing={isEditing}
          isSaving={isSaving} // 🟢 Pass saving state
          demoHighlight={
            enableDemoHighlight &&
            ((step === 1 && !!primaryProfile) || (step === 2 && secondaryProfiles.length > 0) || step === 3)
          } // [UPDATED] Highlight continue when profile selected (step 1 or 2) or generate (step 3) AND guidance enabled
          allowDemoAction={true} // [NEW] Allow all steps within Market Snapshot
        />
      )}
      {renderErrorSB}
    </MDBox>
  );
}

export default MarketSnapshot;
